import {
  CATCHHUB_LOG_EXPORT_FILE_EXTENSION,
  CATCHHUB_LOG_EXPORT_FILE_INVALID_CHAR_REPLACEMENT,
  CATCHHUB_LOG_EXPORT_FILE_LABEL,
  CATCHHUB_LOG_EXPORT_FILE_SEPARATOR,
} from '../../modules/catch-core/data/constants/app.constants';

/** Characters Windows/macOS reject in a file name, plus ASCII control characters. */
const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g;

/**
 * Builds the CatchHub log export file name: `{OrgName}_{OrgId}_Logs_dd_mm_yyyy_hh_mm_ss.csv`.
 *
 * Built in the browser rather than taken from the server's Content-Disposition because the
 * timestamp has to be the downloader's local time, which the server doesn't know.
 *
 * Returns null when either org detail is missing, so the caller can fall back to the server's name
 * instead of saving a file called `_undefined_Logs_...`.
 */
export function buildCatchHubLogExportFileName(orgName: string | null | undefined, orgId: string | null | undefined, now: Date = new Date()): string | null {
  const safeName = sanitizeFileNamePart(orgName);
  const safeId = sanitizeFileNamePart(orgId);
  if (!safeName || !safeId || !now || isNaN(now.getTime())) {
    return null;
  }

  const timestamp = formatFileNameTimestamp(now, CATCHHUB_LOG_EXPORT_FILE_SEPARATOR);

  return [safeName, safeId, CATCHHUB_LOG_EXPORT_FILE_LABEL, timestamp].join(CATCHHUB_LOG_EXPORT_FILE_SEPARATOR) + CATCHHUB_LOG_EXPORT_FILE_EXTENSION;
}

/** Formats `now` in local time as `dd{sep}mm{sep}yyyy{sep}hh{sep}mm{sep}ss`. */
export function formatFileNameTimestamp(now: Date, separator: string): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return [pad(now.getDate()), pad(now.getMonth() + 1), now.getFullYear(), pad(now.getHours()), pad(now.getMinutes()), pad(now.getSeconds())].join(separator);
}

export function sanitizeFileNamePart(value: string | null | undefined): string {
  if (typeof value !== 'string') {
    return '';
  }
  // trailing dots/spaces are silently stripped by Windows, so trim them ourselves for a predictable name
  return value
    .replace(INVALID_FILE_NAME_CHARS, CATCHHUB_LOG_EXPORT_FILE_INVALID_CHAR_REPLACEMENT)
    .trim()
    .replace(/[. ]+$/, '');
}
